import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import MentionEditor from '../builder/MentionEditor';
import { buildMentionables } from '../builder/promptMentions';
import useObjectiveMentions from '../builder/useObjectiveMentions';
import useApi from '../../api';
import { Input } from '@/components/ui/input';
import { InstructionsTabProps } from './types';
import { Bot } from 'lucide-react';
import AgentTabHeader from './AgentTabHeader';

export default function InstructionsTab({
  agId,
  agentName,
  setAgentName,
  form,
  skills,
  goal,
  setGoal,
  persona,
  setPersona
}: InstructionsTabProps) {
  const { get } = useApi();
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);

  // Org users for Team @mentions in the goal/persona editors.
  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const usersResponse = await get('/users/');
        if (!cancelled) setOrganizationUsers(usersResponse.data || []);
      } catch (error) {
        console.error('Error fetching organization users:', error);
      }
    };
    fetchUsers();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const objectives = useObjectiveMentions(agId);
  const mentionables = useMemo(
    () => buildMentionables(form || [], skills || [], { users: organizationUsers, objectives }),
    [form, skills, organizationUsers, objectives]
  );

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={Bot}
        title="Prompting"
        description="Define how your agent identifies itself, who it serves, and how it responds."
      />

      <div className="mx-auto w-full max-w-4xl py-6">
        <div className="overflow-hidden rounded-lg border bg-card divide-y">
          <section className="grid gap-4 p-4 @xl:grid-cols-[190px_minmax(0,1fr)] @xl:gap-8">
            <div>
              <Label htmlFor="agent-name" className="text-sm font-semibold">
                Agent name
              </Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                The internal name your team uses to identify this agent.
              </p>
            </div>
            <Input
              id="agent-name"
              placeholder="Enter your agent name..."
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="w-full"
            />
          </section>

          <section className="p-4">
            <div className="mb-4">
              <Label className="text-sm font-semibold">Base prompt</Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Set the agent's behavior, guardrails, personality, and writing style.
              </p>
            </div>
            <div className="min-w-0">
              <MentionEditor
                value={goal}
                onChange={setGoal}
                mentionables={mentionables}
                placeholder="Enter your base prompt instructions... Type @ to reference fields, skills, or tools."
                className="resize-y overflow-auto font-mono [&_.ProseMirror]:h-full [&_.ProseMirror]:min-h-[300px]"
              />
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                Example: Avoid em dashes and emojis. Use concise, direct language that reflects our brand.
              </p>
            </div>
          </section>

          <section className="p-4">
            <div className="mb-4">
              <Label className="text-sm font-semibold">Ideal customer profile</Label>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                Describe who the agent should recognize and tailor conversations for.
              </p>
            </div>
            <MentionEditor
              value={persona}
              onChange={setPersona}
              mentionables={mentionables}
              placeholder="Describe your ideal customer profiles... Type @ to reference fields, skills, or tools."
              className="min-w-0 font-mono [&_.ProseMirror]:min-h-[140px]"
            />
          </section>
        </div>
      </div>
    </div>
  );
}
